import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import dynamic from 'next/dynamic';
import parse from 'html-react-parser';
import type {StaticImageData} from 'next/image';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import DisplayImage from '@/components/Ui-components/display-image';
import SectionHeader from '@/components/Common/SectionHeader';
import {fadeInUp} from '@/animation/scroll-animation';
import {borderRadius, borderWidth, colors, fontSize, fontWeight, gradients, lineHeight, shadows, transition} from '@/styles/tokens';
import {ICON_TILE_ICON_SIZE, iconTileImageStyle, iconTileSx as sharedIconTileSx} from '@/styles/iconTile';

const AnimatedComponent = dynamic(() => import('../AnimatedComponent').then((mod) => mod.AnimatedComponent));

// ─── Types ────────────────────────────────────────────────────────────────────

/** One labelled block inside a card, e.g. `{label: 'Problem', text: '…'}`. */
export interface ChallengeGridDetail {
    label: string;
    text: string;
}

export interface ChallengeGridCard {
    title: string;
    /** Free-form body. Use `details` instead when the card has labelled blocks. */
    description?: string;
    /** Labelled blocks (Problem / Business Impact / Solution). Takes precedence over `description`. */
    details?: ChallengeGridDetail[];
    /** Rendered inside a navy tile above the title. */
    icon?: React.ReactNode;
    /** Flat monochrome icon image, recoloured for the navy tile. Ignored when `icon` is set. */
    src?: StaticImageData | string;
}

export interface ChallengeGridProps {
    eyebrow?: string;
    heading: string;
    subheading?: string;
    cards: ChallengeGridCard[];
    /** Cards per row on desktop. Defaults to 3. */
    columns?: 2 | 3;
}

// ─── Module-level sx constants ────────────────────────────────────────────────

const gridSx = {
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)'},
    gap: {xs: 2.5, md: 3}
} as const;

const gridTwoColSx = {
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'},
    gap: {xs: 2.5, md: 3}
} as const;

// The tile is navy by default and inverts to white on card hover, so the flat black source
// icons are filtered white first (see iconTileImageStyle), then tinted to colors.primary.deep on hover.
const blueIconFilter =
    'brightness(0) saturate(100%) invert(43.5%) sepia(99.3%) saturate(486.3%) hue-rotate(185deg) brightness(48%) contrast(106.2%)';

const iconTileSx = {
    ...sharedIconTileSx,
    mb: 1.5,
    transition: `background ${transition.base}, color ${transition.base}`
} as const;

const detailLabelSx = {
    fontSize: fontSize[16],
    fontWeight: fontWeight.bold,
    lineHeight: lineHeight[24],
    mb: 0.25,
    transition: `color ${transition.base}`
} as const;

const detailTextSx = {
    fontSize: fontSize[16],
    color: colors.text.primary,
    lineHeight: lineHeight[24],
    transition: `color ${transition.base}`
} as const;

const detailBlockSx = {mb: 1, '&:last-of-type': {mb: 0}} as const;

const fullHeightSx = {height: '100%'} as const;

const cardSx = {
    height: '100%',
    p: {xs: 2, md: 2.5},
    bgcolor: colors.background.default,
    border: `${borderWidth.thick} solid ${colors.neutral.borderLight}`,
    borderRadius: borderRadius.lg,
    boxShadow: shadows.lg,
    backgroundImage: 'none',
    transition: `background-color ${transition.base}, box-shadow ${transition.base}, transform ${transition.base}`,
    '&:hover': {
        backgroundImage: gradients.navyCardDeep,
        transform: 'scale(1.03)',
        boxShadow: shadows.lg,
        '& .font-headers, & .font-regular-text, & .font-regular-text a': {
            color: colors.text.inverse
        },
        '& .challenge-icon-tile': {
            background: colors.background.default,
            color: colors.primary.main
        },
        '& .challenge-icon-image': {
            // Beats the inline white filter on the img element.
            filter: `${blueIconFilter} !important`
        }
    }
} as const;

const cardTitleSx = {
    fontSize: fontSize[18],
    fontWeight: fontWeight.bold,
    lineHeight: lineHeight[28],
    mb: 1,
    transition: `color ${transition.base}`
} as const;

const cardDescSx = {
    fontSize: fontSize[16],
    color: colors.text.primary,
    lineHeight: lineHeight[28],
    transition: `color ${transition.base}`
} as const;

// ─── Component ────────────────────────────────────────────────────────────────

const ChallengeGrid = ({eyebrow, heading, subheading, cards, columns = 3}: ChallengeGridProps) => (
    <Box component="section">
        <MUIContainer>
            <SectionHeader eyebrow={eyebrow} heading={parse(heading)} subheading={subheading ? parse(subheading) : undefined} />
            <Box sx={columns === 2 ? gridTwoColSx : gridSx}>
                {cards.map((card, index) => (
                    <AnimatedComponent key={card.title} variants={fadeInUp} delay={index * 0.1} style={fullHeightSx}>
                        <Box sx={cardSx}>
                            {(card.icon || card.src) && (
                                <Box className="challenge-icon-tile" sx={iconTileSx}>
                                    {card.icon ?? (
                                        <DisplayImage
                                            className="challenge-icon-image"
                                            src={card.src as StaticImageData}
                                            alt=""
                                            width={ICON_TILE_ICON_SIZE}
                                            height={ICON_TILE_ICON_SIZE}
                                            style={iconTileImageStyle}
                                        />
                                    )}
                                </Box>
                            )}
                            <Typography component="h3" className="font-headers" sx={cardTitleSx}>
                                {parse(card.title)}
                            </Typography>
                            {card.details?.length ? (
                                card.details.map((detail) => (
                                    <Box key={detail.label} sx={detailBlockSx}>
                                        <Typography component="p" className="font-headers" sx={detailLabelSx}>
                                            {detail.label}
                                        </Typography>
                                        <Typography component="p" className="font-regular-text remove-underline" sx={detailTextSx}>
                                            {parse(detail.text)}
                                        </Typography>
                                    </Box>
                                ))
                            ) : (
                                <Typography component="p" className="font-regular-text remove-underline" sx={cardDescSx}>
                                    {parse(card.description ?? '')}
                                </Typography>
                            )}
                        </Box>
                    </AnimatedComponent>
                ))}
            </Box>
        </MUIContainer>
    </Box>
);

export default memo(ChallengeGrid);
